import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { modelKeys, toConnection } from "./models";
import type { ApiConnection } from "@/shared/types/models";
import { ApiError } from "./errors";

interface Attempt { id: string; authorization_url: string; expires_in: number }
interface Start { name: string; connection_id?: string }
const terminal = (error: Error | null) => error instanceof ApiError && [400, 403, 404, 409, 422].includes(error.status);

async function startSignIn(body: Start): Promise<Attempt> {
  return unwrap(await client.post<Envelope<Attempt>>("/providers/oauth/claude_code", body));
}

async function cancelSignIn(id: string): Promise<void> {
  await client.delete(`/providers/oauth/claude_code/${id}`);
}

async function completeSignIn(entry: { id: string; code: string }) {
  try {
    return toConnection(unwrap(await client.post<Envelope<ApiConnection>>(
      `/providers/oauth/claude_code/${entry.id}`, { code: entry.code },
    )));
  } finally { entry.code = ""; }
}

/** Authorization codes never enter mutation variables, query caches or browser storage. */
export function useClaudeSignIn() {
  const qc = useQueryClient();
  const entry = useRef<{ id: string; code: string } | null>(null);
  const pending = useRef<string | null>(null);
  const mounted = useRef(true);
  const start = useMutation({
    mutationFn: async (body: Start) => {
      const attempt = await startSignIn(body);
      if (mounted.current) pending.current = attempt.id;
      else await cancelSignIn(attempt.id);
      return attempt;
    },
    gcTime: 0, meta: { silent: true },
  });
  const finish = useMutation({
    mutationFn: async () => {
      if (!entry.current) throw new Error("Start sign-in again.");
      try {
        const connection = await completeSignIn(entry.current);
        pending.current = null;
        return connection;
      } finally { entry.current = null; }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: modelKeys.all }),
    gcTime: 0, meta: { silent: true },
  });
  const cancel = useMutation({ mutationFn: cancelSignIn, gcTime: 0, meta: { silent: true } });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (entry.current) entry.current.code = "";
      if (pending.current) void cancelSignIn(pending.current).catch(() => {
        // The server expires abandoned attempts after ten minutes.
      });
    };
  }, []);
  return {
    start, finish, cancel, stopped: terminal(finish.error),
    complete: (id: string, code: string) => { entry.current = { id, code }; return finish.mutateAsync(); },
    reset: async () => {
      if (pending.current) await cancel.mutateAsync(pending.current);
      pending.current = null;
      start.reset(); finish.reset(); cancel.reset();
    },
  };
}
